import { create } from 'zustand';

export type InteractionState = 'idle' | 'pressed' | 'pending' | 'success' | 'error' | 'disabled';

export interface WidgetInteraction {
  state: InteractionState;
  intentId?: string;
  timestamp: number;
  error?: string;
}

interface InteractionStoreState {
  interactions: Map<string, WidgetInteraction>;
  setInteraction: (widgetKey: string, interaction: Partial<WidgetInteraction>) => void;
  clearInteraction: (widgetKey: string) => void;
  getInteraction: (widgetKey: string) => WidgetInteraction;
  setPending: (widgetKey: string, intentId: string) => void;
  setSuccess: (widgetKey: string) => void;
  setError: (widgetKey: string, error: string) => void;
  clearByIntentId: (intentId: string) => void;
}

export const useInteractionStore = create<InteractionStoreState>((set, get) => ({
  interactions: new Map(),
  
  setInteraction: (widgetKey: string, interaction: Partial<WidgetInteraction>) => {
    set((state) => {
      const newInteractions = new Map(state.interactions);
      const current = newInteractions.get(widgetKey) || { state: 'idle', timestamp: Date.now() };
      newInteractions.set(widgetKey, { ...current, ...interaction, timestamp: Date.now() });
      return { interactions: newInteractions };
    });
  },
  
  clearInteraction: (widgetKey: string) => {
    set((state) => {
      const newInteractions = new Map(state.interactions);
      newInteractions.delete(widgetKey);
      return { interactions: newInteractions };
    });
  },
  
  getInteraction: (widgetKey: string): WidgetInteraction => {
    const interaction = get().interactions.get(widgetKey);
    return interaction || { state: 'idle', timestamp: Date.now() };
  },
  
  setPending: (widgetKey: string, intentId: string) => {
    get().setInteraction(widgetKey, { state: 'pending', intentId });
  },
  
  setSuccess: (widgetKey: string) => {
    get().setInteraction(widgetKey, { state: 'success' });
    setTimeout(() => {
      const current = get().getInteraction(widgetKey);
      if (current.state === 'success') {
        get().setInteraction(widgetKey, { state: 'idle' });
      }
    }, 600);
  },
  
  setError: (widgetKey: string, error: string) => {
    get().setInteraction(widgetKey, { state: 'error', error });
    setTimeout(() => {
      const current = get().getInteraction(widgetKey);
      if (current.state === 'error') {
        get().setInteraction(widgetKey, { state: 'idle' });
      }
    }, 1200);
  },
  
  clearByIntentId: (intentId: string) => {
    set((state) => {
      const newInteractions = new Map(state.interactions);
      for (const [key, interaction] of newInteractions.entries()) {
        if (interaction.intentId === intentId) {
          newInteractions.delete(key);
        }
      }
      return { interactions: newInteractions };
    });
  }
}));
